import React, { useState, useEffect, useCallback, memo } from "react";
import { useIntl } from "react-intl";
import { DateTime } from "luxon";
import { Box, IconButton, Theme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { makeStyles } from "tss-react/mui";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import ReplayIcon from "@mui/icons-material/Replay";

import { GenericObject } from "../services/Price";
import { isObject } from "../services/utils";
import { QuickSearchFilter } from "./filters/QuickSearch";
import { SelectFilter } from "./filters/SelectFilter";
import { UnmappedFilter } from "./filters/UnmappedFilter";
import { DuplicateFilter } from "./filters/DuplicateFilter";
import {
  ConfigurationTableFilter,
  ConfigurationTableFilterType,
  FilterDatePicker,
  Option,
  FilterSelect,
  FilterSelectMultiple,
  FilterSelectMultipleApi,
} from "./interfaces";
import CustomDatePicker from "../components/Inputs/DatePickers/CustomDatePicker";
import { DatePickerVariant } from "../components/Inputs/DatePickers/styles";
import DayOfWeekSelector from "../Calendar/Filter/DayOfWeekSelector";
import { SelectMultipleFilter } from "./filters/SelectMultipleFilter";
import StaticSelectorFilter from "./filters/StaticSelectorFilter";
import SelectMultipleApiFilter from "./filters/SelectMultipleApiFilter";

const useStyles = makeStyles()((theme: Theme) => ({
  filterTypeInput: {
    flex: "0 0 224px",
    borderRadius: "6px",
    backgroundColor: theme.vars.palette.background.input,
    border: `1px solid ${theme.vars.palette.border.inputOutlined}`,
    // The wrapper is the field: the inner input keeps no fill of its own.
    "& .MuiInputBase-root": {
      backgroundColor: "transparent",
    },
    "& .MuiInput-underline:before, & .MuiInput-underline:after": {
      display: "none",
    },
    "& input": {
      fontSize: "0.875rem",
    },
    "& .MuiButtonBase-root": {
      color: theme.vars.palette.text.secondary,
    },
    "& .MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot":
      {
        paddingRight: 0,
      },
  },
  filterTypeDatePicker: {
    flex: "0 0 203px",
    minWidth: 0,
  },
  filterContrastContainer: {
    "& > div": {
      padding: 0,
      flex: "0 0 calc((100% / 3) - 6px)",
    },
    "& .MuiFormControl-root, & .MuiPickersTextField-root": {
      margin: 0,
    },
    "& .MuiInputBase-root.MuiInput-root, & .MuiFormControl-root .MuiInputBase-root.MuiOutlinedInput-root":
      {
        backgroundColor: theme.vars.palette.background.input,
        borderRadius: "6px !important",
        color: theme.vars.palette.text.secondary,
        padding: "6px 9px 6px 6px",

        "& .MuiInputBase-input.MuiInput-input": {
          padding: "2.5px 4px 2.5px 8px",
        },
        "& .MuiIconButton-root": {
          right: "9px",
        },
      },
  },
}));

const SX_DAYS_SELECTOR = {
  "& .MuiFormControl-root": {
    "& input": {
      fontSize: "0.875rem",
    },
    "&.MuiFormControl-marginDense.MuiFormControl-fullWidth.MuiTextField-root": {
      marginTop: 1,
      marginBottom: 0.5,
    },
    "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input": {
      padding: "2px 4px 3px 0",
    },
    "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-formControl.MuiInputBase-sizeSmall.MuiAutocomplete-inputRoot":
      {
        padding: "0 30px 1px 0",
        height: "26px",
        overflow: "hidden",
      },
  },
};

export type ConfigurationFilterModelType = {
  [key in ConfigurationTableFilterType]?: any;
};

interface FilterMapProps {
  filters: GenericObject;
  filter: ConfigurationTableFilter;
  orderingFilterValue?: Option | null;
  handleFilterChange: (filter: GenericObject, value: any) => void;
  updateFilterOptions?: (options: Option) => void;
  datePickerVariant?: DatePickerVariant;
}

const TableFilter = ({
  filters,
  filter,
  orderingFilterValue,
  handleFilterChange,
  updateFilterOptions,
  datePickerVariant,
}: FilterMapProps) => {
  const { classes } = useStyles();
  const { formatMessage } = useIntl();

  const handleChange = useCallback(
    (value: any) => {
      handleFilterChange(filter, value);
    },
    [handleFilterChange, filter]
  );

  switch (filter.type) {
    case ConfigurationTableFilterType.Unmapped:
      return (
        <UnmappedFilter
          config={{
            api: filter.api,
          }}
          onClick={handleChange}
          value={filters.is_unmapped}
        />
      );
    case ConfigurationTableFilterType.Unlocated:
      return (
        <UnmappedFilter
          config={{
            api: filter.api,
          }}
          onClick={handleChange}
          value={filters.is_unlocated}
          icon={<LocationOffIcon />}
          tooltip="UNLOCATED_PROPERTIES"
        />
      );
    case ConfigurationTableFilterType.WithPastDate:
      return (
        <UnmappedFilter
          config={{
            api: filter.api,
          }}
          onClick={handleChange}
          value={filters.is_with_past_date}
          icon={<NotificationsIcon />}
          tooltip="MODELS_WITH_PAST_DATE"
        />
      );
    case ConfigurationTableFilterType.Duplicate:
      return (
        <DuplicateFilter
          config={{
            api: filter.api,
          }}
          onClick={handleChange}
          value={filters.is_duplicate}
        />
      );
    case ConfigurationTableFilterType.Select:
      return (
        <Box className={classes.filterTypeInput} sx={{ px: 1 }}>
          <SelectFilter
            config={{
              api: filter.api,
              label: filter.label,
              labelKey: filter.labelKey,
              type: filter.type,
              apiParams: filter.apiParams,
            }}
            orderingFilterValue={orderingFilterValue}
            filterValue={filters[filter.lookup] ?? null}
            filters={filters}
            filter={filter as FilterSelect}
            onChange={handleChange}
            updateFilterOptions={updateFilterOptions}
          />
        </Box>
      );
    case ConfigurationTableFilterType.SelectMultiple:
      return (
        <Box
          className={classes.filterTypeInput}
          sx={{ flex: "0 0 250px", px: 1 }}
        >
          <SelectMultipleFilter
            config={{
              api: filter.api,
              label: filter.label,
              labelKey: filter.labelKey,
              type: filter.type,
              apiParams: filter.apiParams,
            }}
            mustTranslateOptions={filter.mustTranslateOptions}
            filterValue={filters[filter.lookup] ?? null}
            filters={filters}
            filter={filter as FilterSelectMultiple}
            onChange={handleChange}
            updateFilterOptions={updateFilterOptions}
          />
        </Box>
      );
    case ConfigurationTableFilterType.Search:
      return (
        <QuickSearchFilter
          onChange={handleChange}
          value={filters.search ?? null}
        />
      );
    case ConfigurationTableFilterType.DatePicker: {
      const dpFilter = filter as FilterDatePicker;
      const minDateStr = dpFilter.minDateField
        ? filters[dpFilter.minDateField]
        : undefined;
      const maxDateStr = dpFilter.maxDateField
        ? filters[dpFilter.maxDateField]
        : undefined;

      return (
        <Box className={classes.filterTypeDatePicker}>
          <CustomDatePicker
            label={dpFilter.label}
            value={filters[dpFilter.lookup] ?? null}
            minDate={
              typeof minDateStr === "string"
                ? DateTime.fromISO(minDateStr)
                : undefined
            }
            maxDate={
              typeof maxDateStr === "string"
                ? DateTime.fromISO(maxDateStr)
                : undefined
            }
            views={dpFilter.views}
            dense
            variant={datePickerVariant}
            onChange={handleChange}
          />
        </Box>
      );
    }
    case ConfigurationTableFilterType.DayOfWeek:
      return (
        <Box className={classes.filterTypeInput} sx={{ px: 1 }}>
          <DayOfWeekSelector
            onChange={handleChange}
            value={filters[filter.lookup] || []}
            placeholder={formatMessage({ id: filter.label })}
            sx={SX_DAYS_SELECTOR}
          />
        </Box>
      );
    case ConfigurationTableFilterType.StaticSelect:
      return (
        <Box className={classes.filterTypeInput} sx={{ px: 1 }}>
          <StaticSelectorFilter
            filter={filter}
            filterValue={filters[filter.lookup] ?? null}
            onChange={handleChange}
          />
        </Box>
      );
    case ConfigurationTableFilterType.ResetButton:
      return (
        <IconButton
          aria-label="reset filter"
          disableRipple
          onClick={() => handleChange(null)}
          sx={{
            color: "text.secondary",
            height: "38px",
            width: "38px",
          }}
        >
          <ReplayIcon />
        </IconButton>
      );
    case ConfigurationTableFilterType.SelectMultipleApi:
      return (
        <Box
          className={classes.filterTypeInput}
          sx={{ flex: "0 0 250px", px: 1 }}
        >
          <SelectMultipleApiFilter
            filters={filters}
            filter={filter as FilterSelectMultipleApi}
            onChange={handleChange}
          />
        </Box>
      );
    default:
      return null;
  }
};

const EXCLUSIVE_BOOLEAN_LOOKUPS = [
  "is_unmapped",
  "is_unlocated",
  "is_with_past_date",
  "is_duplicate",
];

/**
 * Get lookup value for filter.
 */
const getLookup = (filter: ConfigurationTableFilter) => {
  switch (filter.type) {
    case ConfigurationTableFilterType.Search:
      return "search";
    case ConfigurationTableFilterType.Unmapped:
      return "is_unmapped";
    case ConfigurationTableFilterType.Unlocated:
      return "is_unlocated";
    case ConfigurationTableFilterType.WithPastDate:
      return "is_with_past_date";
    case ConfigurationTableFilterType.Duplicate:
      return "is_duplicate";
    case ConfigurationTableFilterType.ResetButton:
      return ConfigurationTableFilterType.ResetButton;
    case ConfigurationTableFilterType.Select:
      return filter.lookup;
    case ConfigurationTableFilterType.SelectMultiple:
      return filter.lookup;
    case ConfigurationTableFilterType.DatePicker:
      return filter.lookup;
    case ConfigurationTableFilterType.DayOfWeek:
      return filter.lookup;
    case ConfigurationTableFilterType.StaticSelect:
      return filter.lookup;
    case ConfigurationTableFilterType.SelectMultipleApi:
      return filter.lookup;
    default:
      return null;
  }
};

export const Filters = memo(
  ({
    tableFilter,
    onChange,
    updateFilterOptions,
    orderingFilterValue,
    contrastedStyles,
    initialUrlFilter,
    datePickerVariant,
  }: {
    tableFilter: ConfigurationTableFilter[];
    onChange: (filters: GenericObject) => void;
    updateFilterOptions?: (options: Option) => void;
    orderingFilterValue?: Option | null;
    contrastedStyles?: boolean;
    initialUrlFilter?: Record<string, string>;
    datePickerVariant?: DatePickerVariant;
  }) => {
    const { classes } = useStyles();
    const initialFilter = tableFilter.reduce<GenericObject>((acc, f) => {
      if (
        f.type === ConfigurationTableFilterType.DatePicker &&
        f.defaultToday
      ) {
        acc[f.lookup] = DateTime.now().toISODate();
      }
      return acc;
    }, {});

    if (initialUrlFilter) {
      const ARRAY_TYPES = new Set([
        ConfigurationTableFilterType.SelectMultiple,
        ConfigurationTableFilterType.SelectMultipleApi,
        ConfigurationTableFilterType.DayOfWeek,
      ]);

      tableFilter.forEach((f) => {
        const lookup = getLookup(f);
        if (!lookup || lookup === ConfigurationTableFilterType.ResetButton)
          return;
        const raw = initialUrlFilter[lookup];
        if (!raw) return;
        if (ARRAY_TYPES.has(f.type)) {
          const toNumeric =
            f.type === ConfigurationTableFilterType.DayOfWeek ||
            ("numericIds" in f && f.numericIds);
          initialFilter[lookup] = raw
            .split(",")
            .map((v) => (toNumeric && !isNaN(Number(v)) ? Number(v) : v));
        } else if (
          f.type === ConfigurationTableFilterType.Unmapped ||
          f.type === ConfigurationTableFilterType.Unlocated ||
          f.type === ConfigurationTableFilterType.WithPastDate ||
          f.type === ConfigurationTableFilterType.Duplicate
        ) {
          initialFilter[lookup] = raw === "true";
        } else {
          const toNumeric = "numericIds" in f && f.numericIds;
          initialFilter[lookup] =
            toNumeric && !isNaN(Number(raw)) ? Number(raw) : raw;
        }
      });
    }

    const [filters, setFilters] = useState<{
      filter: GenericObject | GenericObject[];
      rawFilter: GenericObject | GenericObject[];
    }>({
      filter: initialFilter,
      rawFilter: initialFilter,
    });

    const handleChange = useCallback(
      (filter: GenericObject, values: any) => {
        const lookup = getLookup(filter as ConfigurationTableFilter);

        if (lookup) {
          if (lookup === ConfigurationTableFilterType.ResetButton) {
            setFilters((prev) => {
              const newFilter: {
                [k: string]: null | string;
              } = Object.fromEntries(
                Object.keys(prev.filter).map((key) => [key, null])
              );

              // Restore defaultToday filters to today's date
              tableFilter.forEach((f) => {
                if (
                  f.type === ConfigurationTableFilterType.DatePicker &&
                  f.defaultToday
                ) {
                  newFilter[f.lookup] = DateTime.now().toISODate();
                }
              });

              return {
                filter: newFilter,
                rawFilter: newFilter,
              };
            });
          } else {
            // These each swap the table for a different subset, so turning one
            // on clears the others rather than intersecting with them.
            const exclusiveReset: GenericObject =
              values === true && EXCLUSIVE_BOOLEAN_LOOKUPS.includes(lookup)
                ? Object.fromEntries(
                    EXCLUSIVE_BOOLEAN_LOOKUPS.filter(
                      (other) => other !== lookup
                    ).map((other) => [other, null])
                  )
                : {};

            if (Array.isArray(values)) {
              return setFilters((prev) => ({
                filter: {
                  ...prev.filter,
                  [lookup]: values.map((v) =>
                    isObject(v)
                      ? filter.useLabelValueForLookup
                        ? v.label
                        : v.id
                      : v
                  ),
                },
                rawFilter: {
                  ...prev.rawFilter,
                  [lookup]: values.map((v) => {
                    return isObject(v)
                      ? { id: v.id, [filter.labelKey]: v.label }
                      : v;
                  }),
                },
              }));
            }

            setFilters((prev) => ({
              filter: {
                ...prev.filter,
                ...exclusiveReset,
                [lookup]: isObject(values)
                  ? filter.useLabelValueForLookup
                    ? values.label
                    : values.id
                  : values,
              },
              rawFilter: {
                ...prev.rawFilter,
                ...exclusiveReset,
                [lookup]: isObject(values)
                  ? {
                      id: values.id,
                      [filter.labelKey]: values.label,
                    }
                  : values,
              },
            }));
          }
        }
      },
      [tableFilter]
    );

    useEffect(() => {
      onChange(filters);
    }, [filters, onChange]);

    return (
      <Grid
        container
        className={`
        ${contrastedStyles ? classes.filterContrastContainer : ""}
        `}
        sx={{
          gap: 1,
          alignItems: "center",
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        {tableFilter.map((filter, index) => (
          <TableFilter
            filters={filters.filter}
            filter={filter}
            orderingFilterValue={orderingFilterValue}
            handleFilterChange={handleChange}
            key={`${filter.type}-${index}`}
            updateFilterOptions={updateFilterOptions}
            datePickerVariant={datePickerVariant}
          />
        ))}
      </Grid>
    );
  }
);
